/**
 * <AuthGuard> — gates routes behind an authenticated session.
 *
 * On mount: hits `/users/me`. Success → stores the user in SessionContext
 * and renders children. 401 → redirect to `/login` (preserves the
 * originally-requested location in router state so login can bounce
 * back). Any other failure → renders the fallback (caller may pass
 * one) so a transient outage doesn't bounce the user out.
 *
 * Dev-mode escape hatch: when `authBypass` is on (DevPanel toggle,
 * sourced from `VITE_VOLLGAS_DEV_MODE`), the guard renders children
 * without calling `/me`. Useful for offline UI work; release builds
 * ship with `VOLLGAS_DEV_MODE=false` so this branch is dead code.
 */
import { useEffect, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useLocation } from 'react-router-dom'
import { ApiError } from '@/apis/apiClient'
import { authApi } from '@/apis/workshopApis'
import type { AuthUser } from '@/apis/types'
import { useI18n } from '@/i18n/TranslationContext'
import { useBypassFlags } from './bypassContext'
import { landingFor, TECHNICAL_ALLOWED } from './landingFor'
import { useSession } from './SessionContext'

export const SESSION_QUERY_KEY = ['auth', 'me'] as const

/**
 * Dev-bypass session — mirrors the MSW fixtures' technician (`USR_TECH` /
 * `meTechnicianFixture` in `test/msw/fixtures.ts`) so a note authored "by
 * me" under MSW still reads as mine when auth is bypassed, and
 * `useCurrentUser()` isn't null on a surface that assumes a signed-in tech.
 * A literal, not an import: `AuthGuard` is a real prod component (see
 * `bypassContext.ts`), and importing from `src/test/` would pull
 * test-only code into the production bundle for a value that's five
 * fields.
 */
const DEV_BYPASS_USER: AuthUser = {
  id: 'USR-TECH-01',
  email: 'mehmet@vollgas.at',
  emailVerified: true,
  firstName: 'Mehmet',
  lastName: 'Köse',
  displayName: 'Mehmet Köse',
  language: 'de',
  role: { key: 'TECHNICIAN', category: 'STAFF' },
  capabilities: ['workshop.work-order.read', 'workshop.work-order.write'],
}

interface AuthGuardProps {
  readonly children: ReactNode
  /** Render this on transient (non-401) failure. */
  readonly fallback?: ReactNode
}

export function AuthGuard({ children, fallback }: AuthGuardProps) {
  const { authBypass } = useBypassFlags()
  const { setUser } = useSession()
  const { t } = useI18n()
  const location = useLocation()

  const enabled = !authBypass
  const query = useQuery<AuthUser, Error>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: authApi.me,
    enabled,
    retry: (failureCount, err) => {
      // Don't retry 401 — it's the explicit "you're not signed in" answer.
      if (err instanceof ApiError && err.status === 401) return false
      return failureCount < 2
    },
    staleTime: 60_000,
  })

  useEffect(() => {
    if (query.data) setUser(query.data)
  }, [query.data, setUser])

  // Dev-bypass escape hatch: with no `/me` call to populate the session,
  // every consumer of useCurrentUser() (the note badge, "who owns this"
  // labels, the claim/take affordances) saw null and the workspace behaved
  // as if nobody were signed in. Strictly inside this branch — production
  // is untouched.
  useEffect(() => {
    if (authBypass) setUser(DEV_BYPASS_USER)
  }, [authBypass, setUser])

  if (authBypass) return <>{children}</>

  if (query.isLoading) {
    return (
      <div role="status" className="py-12 text-center text-sm text-glass-text-muted">
        {t.home.loading}
      </div>
    )
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 401) {
      return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
    }
    return (
      <>{fallback ?? <div role="alert" className="py-12 text-center text-sm text-glass-text-muted">{t.home.errorPanel}</div>}</>
    )
  }

  if (!query.data) return null

  // Role gate: ADMIN / MANAGER / TECHNICIAN may use the technical
  // workspace. Anything else (CUSTOMER, SHADOW, unknown) gets a
  // hard redirect to its own landing — see ./landingFor. Customers
  // who are *also* staff are picked up by their staff role here
  // (PermissionEvaluator returns the most-privileged active role).
  const roleKey = query.data.role?.key
  if (!roleKey || !TECHNICAL_ALLOWED.has(roleKey)) {
    if (typeof window !== 'undefined') {
      window.location.replace(landingFor(roleKey))
    }
    return null
  }

  return <>{children}</>
}
